import {AfterViewInit, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {StylingUtils} from 'app/shared/styling-utils';
import {UnitPublicService} from '../../../services/public/unit-public.service';
import {Router} from '@angular/router';
import {MatButtonToggleChange} from '@angular/material/button-toggle';
import {Subject, takeUntil} from 'rxjs';
import { ViewportService } from 'app/services/viewport.service';
import { GoogleMapsLoaderService } from 'app/services/google-maps-loader.service';


class LagMarker {
    name: string;
    markerPosition: google.maps.LatLngLiteral;
    id: string;
}

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnDestroy {
    @Input() pretitle = '';
    @Input() title = '';
    mobile: boolean = false;
    mobileDisplay: 'list' | 'layout' = 'list';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    userLocation: { lat: number; lng: number } = {lat: 47.3876821, lng: 12.783111};

    center: { lat: number; lng: number } = {lat: 47.3876821, lng: 12.783111};
    locations: any[] = [];
    // Gefilterte Optionen fürs Standort-Autocomplete (per Texteingabe durchsuchbar)
    filteredLocations: any[] = [];
    highlightItem: any;

    SALZBURG_BOUNDS: any = {
        north: 49.913404,
        south: 45.676518,
        west: 9.5,
        east: 17.5,
    };
    markers: LagMarker[] = [];
    options: google.maps.MapOptions = {
        zoom: 10,
        minZoom: 8,
        maxZoom: 18,
        center: {
            lat: 47.309311,
            lng: 12.794729,
        },
        styles: [
            {
                stylers: [
                    {
                        hue: '#ff1a00',
                    },
                    {
                        invert_lightness: true,
                    },
                    {
                        saturation: -100,
                    },
                    {
                        lightness: 33,
                    },
                    {
                        gamma: 0.5,
                    },
                ],
            },
            {
                featureType: 'water',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#2D333C',
                    },
                ],
            },
        ],
        streetViewControl: false,
        mapTypeControl: false,
        restriction: {
            latLngBounds: this.SALZBURG_BOUNDS,
            strictBounds: false,
        },
    };
    markerOptions: google.maps.MarkerOptions;
    /** true, sobald die Maps-API nachgeladen ist – erst dann wird <google-map> gerendert */
    apiLoaded = false;

    constructor(
        private locationService: UnitPublicService,
        private router: Router,
        private viewport: ViewportService,
        private mapsLoader: GoogleMapsLoaderService,
    ) {}

    ngOnInit(): void {
        this.mapsLoader.load().then((ok) => {
            if (!ok) {
                return;
            }
            this.markerOptions = {
                    draggable: false,
                    icon: {
                        path: 'M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z',
                        fillColor: StylingUtils.getPrimaryColor(),
                        fillOpacity: 1,
                        strokeWeight: 1,
                        strokeColor: StylingUtils.getPrimaryColor(),
                        scale: 2,
                        anchor: new google.maps.Point(15, 30),
                    },
            };
            this.apiLoaded = true;
        });
        this.viewport.isMobile$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((mobile) => (this.mobile = mobile));
        this.locationService.getLocations().subscribe((data: any) => {
            this.locations = data;
            this.filteredLocations = data;
            this.setLocations(data);
            this.setUserLocation();
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    setLocations(locations: any[]): void {
        for (const loc of locations) {
            if (loc.gpsLocation) {
                //Add to map
                this.markers.push({
                    name: loc.name,
                    id: loc._id,
                    markerPosition: {
                        lat: loc.gpsLocation.latitude,
                        lng: loc.gpsLocation.longitude,
                    }
                });
            }
        }
    }

    markerClicked(marker: any): void {
        this.router.navigateByUrl(
            `/home/booking?location=${marker.id}`,
        );
    }

    filterLocations(query: string): void {
        const q = (query || '').toLowerCase();
        this.filteredLocations = this.locations.filter((l) => l?.name?.toLowerCase().includes(q));
    }

    onLocationInput(event: any): void {
        this.filterLocations(event?.target?.value);
    }

    onLocationFocus(inputEl: HTMLInputElement): void {
        // Beim Fokussieren die volle Liste zeigen und Text markieren, damit man direkt weitertippen kann
        this.filterLocations('');
        inputEl?.select();
    }

    locationSelected(location: any, inputEl?: HTMLInputElement): void {
        if (!location?._id) { return; }
        // Nicht navigieren: die Liste/das Carousel springt zur gewählten Standort-Karte
        this.highlightItem = {id: location._id};
        if (location?.gpsLocation) {
            this.panTo({lat: location.gpsLocation.latitude, lng: location.gpsLocation.longitude});
        }
        if (inputEl) {
            inputEl.value = location?.name || '';
        }
    }

    setUserLocation(): void {
        if (navigator.geolocation) {
            navigator.geolocation.watchPosition((position) => {
                const longitude = position.coords.longitude;
                const latitude = position.coords.latitude;
                this.userLocation = {
                    lat: latitude,
                    lng: longitude
                };
                this.center = {
                    lat: latitude,
                    lng: longitude
                };
            }, (error: any) => {
                console.error('MAP-Component Error:', error);
                this.userLocation = null;
            }, {
                enableHighAccuracy: true,
                timeout: 8000,
                maximumAge: 0
            });
        } else {
            console.log('No support for geolocation');
            this.userLocation = null;
        }
    }

    switchView(event: MatButtonToggleChange): void {
        this.mobileDisplay = event?.value;
        if (this.mobileDisplay === 'layout') {
            this.setLocations(this.locations);
        }
    }

    panTo(event: { lat: number; lng: number }): void {
        this.center = {
            lat: event.lat,
            lng: event.lng
        };
    }

    highlightList(event): void {
        this.highlightItem = event;
    }
}
